import { stripEntityVersionPathSuffix } from "@repo/shared";
import * as Sentry from "@sentry/react";
import { Suspense, useMemo, useState } from "react";
import { ApplicationVersionErrorFallback } from "@/components/application-version-error-fallback";
import { GenericErrorFallback } from "@/components/generic-error-fallback";
import { ServerConnectionLostFallback } from "@/components/server-connection-lost-fallback";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { isChunkLoadError, isServerConnectionError } from "@/lib/connection-errors";
import { isAuthError } from "@/lib/convex-errors";
import { AssistantKeepAlive, isAssistantPageRouteKey } from "../routing/assistant-keep-alive";
import { useProjectRoute } from "../routing/project-route-context";
import { createProjectRouteViewLoaders, resolveProjectRouteView } from "../routing/view-registry";

interface ProjectContentProps {
  projectId: string;
}

/**
 * ProjectContent renders the appropriate view based on currentPath.
 *
 * Uses a declarative route map instead of TanStack Router's <Outlet />.
 * Each view receives projectId as a prop, with dynamic params extracted
 * from the URL path via simple string matching (no regex).
 */
export function ProjectContent({ projectId }: ProjectContentProps) {
  const {
    parsed: resolvedPath,
    isFrameworkPending: isProjectFrameworkPending,
    isFrameworkMissing: isProjectFrameworkMissing,
    frameworkStructure,
  } = useProjectRoute();
  const { flags: featureFlags, status: featureFlagStatus } = useFeatureFlags();
  const [routeLoadRetryAttempt, setRouteLoadRetryAttempt] = useState(0);
  const routeComponents = useMemo(
    () => createProjectRouteViewLoaders(routeLoadRetryAttempt),
    [routeLoadRetryAttempt]
  );

  const entityBasePath =
    resolvedPath.routeKey === "entity"
      ? (resolvedPath.entityRouteLocation?.baseEntityPath ??
        resolvedPath.baseEntityPath ??
        (resolvedPath.versionIntent
          ? stripEntityVersionPathSuffix(resolvedPath.canonicalPath)
          : resolvedPath.canonicalPath))
      : undefined;
  const entityVersionIntent =
    resolvedPath.routeKey === "entity"
      ? resolvedPath.entityRouteLocation?.intent.kind === "current"
        ? undefined
        : (resolvedPath.entityRouteLocation?.intent ?? resolvedPath.versionIntent)
      : undefined;
  const entityTabSlug = resolvedPath.routeKey === "entity" ? resolvedPath.entityTabSlug : undefined;

  /**
   * `featureFlags` is a dependency beside `featureFlagStatus` because a revoke or a re-grant
   * while the page is open is a ready-to-ready change of the value alone: keying on the status
   * would hold a gated page up after its flag went away. The map's identity moves with its
   * contents — the hook hands back one object per resolved answer.
   */
  const resolution = useMemo(
    () =>
      resolveProjectRouteView({
        components: routeComponents,
        routeKey: resolvedPath.routeKey,
        params: resolvedPath.params,
        normalizedPath: resolvedPath.normalizedPath,
        compiledCatalog: frameworkStructure?.compiledCatalog ?? null,
        versionIntent: entityVersionIntent,
        entityTabSlug,
        baseEntityPath: entityBasePath,
        featureFlags,
        featureFlagStatus,
      }),
    [
      entityBasePath,
      entityTabSlug,
      entityVersionIntent,
      featureFlags,
      featureFlagStatus,
      frameworkStructure?.compiledCatalog,
      resolvedPath,
      routeComponents,
    ]
  );
  const shouldShowAssistantPage = isAssistantPageRouteKey(resolvedPath.routeKey);

  if (isProjectFrameworkMissing) {
    throw new Error("Project framework structure is not available.");
  }

  if (isProjectFrameworkPending) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <ContentSkeleton />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <ProjectContentErrorFallback
            error={error}
            resetError={resetError}
            resetServerConnectionError={() => {
              setRouteLoadRetryAttempt((attempt) => attempt + 1);
              resetError();
            }}
          />
        )}
      >
        <Suspense fallback={<ContentSkeleton />}>
          <AssistantKeepAlive
            projectId={projectId}
            AssistantPage={routeComponents.AiAssistantPage}
          />
          {shouldShowAssistantPage ? null : resolution.kind === "pending" ? (
            // A gated route with no answer yet: the same skeleton the framework structure
            // shows, and nothing mounted that would query a feature the caller may not hold.
            <ContentSkeleton />
          ) : (
            <resolution.Component
              key={resolution.remountKey}
              projectId={projectId}
              {...resolution.params}
            />
          )}
        </Suspense>
      </Sentry.ErrorBoundary>
    </div>
  );
}

export function ProjectContentErrorFallback({
  error,
  resetError,
  resetServerConnectionError,
}: {
  error: unknown;
  resetError?: () => void;
  resetServerConnectionError?: () => void;
}) {
  if (isAuthError(error)) {
    throw error;
  }

  if (isChunkLoadError(error)) {
    return <ApplicationVersionErrorFallback />;
  }

  if (isServerConnectionError(error)) {
    return <ServerConnectionLostFallback resetError={resetServerConnectionError ?? resetError} />;
  }

  return <GenericErrorFallback resetError={resetError} />;
}

function ContentSkeleton() {
  return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-8 w-1/4" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}
